/**
 * Journal entry form (spec §8.7).
 *
 * Presentational component: mood / energy / stress 5-point selectors, the
 * three guided prompts, free writing and optional tags. The page owns state,
 * dirty tracking, sync truth and persistence; this form never talks to
 * repositories and never logs journal content.
 */
import { TextArea } from '@/components/ui'
import { DropletIcon, HeartIcon, MoonIcon } from '@/components/icons'
import { ScaleSelector } from './ScaleSelector'
import { TagsInput } from './TagsInput'
import {
  ENERGY_OPTIONS,
  MOOD_OPTIONS,
  STRESS_OPTIONS,
  type JournalFormValues,
} from './scaleOptions'

export type JournalEntryFormProps = {
  values: JournalFormValues
  onChange: (values: JournalFormValues) => void
  disabled?: boolean
}

export function JournalEntryForm({ values, onChange, disabled = false }: JournalEntryFormProps) {
  const patch = (partial: Partial<JournalFormValues>) => onChange({ ...values, ...partial })

  return (
    <div className="journal-form">
      <div className="journal-scales">
        <ScaleSelector
          legend="Stemming"
          icon={<HeartIcon size={20} />}
          name="mood"
          value={values.mood}
          onChange={(mood) => patch({ mood })}
          options={MOOD_OPTIONS}
          disabled={disabled}
        />
        <ScaleSelector
          legend="Energie"
          icon={<DropletIcon size={20} />}
          name="energy"
          value={values.energy}
          onChange={(energy) => patch({ energy })}
          options={ENERGY_OPTIONS}
          disabled={disabled}
        />
        <ScaleSelector
          legend="Stress"
          icon={<MoonIcon size={20} />}
          name="stress"
          value={values.stress}
          onChange={(stress) => patch({ stress })}
          options={STRESS_OPTIONS}
          disabled={disabled}
        />
      </div>

      <div className="journal-prompts">
        <TextArea
          label="Wat ging er goed?"
          value={values.wentWell}
          onChange={(wentWell) => patch({ wentWell })}
          rows={3}
          placeholder="Klein of groot, alles telt."
        />
        <TextArea
          label="Wat was moeilijk?"
          value={values.wasDifficult}
          onChange={(wasDifficult) => patch({ wasDifficult })}
          rows={3}
          placeholder="Wat kostte vandaag energie?"
        />
        <TextArea
          label="Wat wil ik morgen onthouden?"
          value={values.rememberTomorrow}
          onChange={(rememberTomorrow) => patch({ rememberTomorrow })}
          rows={3}
          placeholder="Eén ding om mee te nemen naar morgen."
        />
        <TextArea
          label="Vrij schrijven"
          value={values.freewriting}
          onChange={(freewriting) => patch({ freewriting })}
          rows={6}
          placeholder="Schrijf hier alles wat je kwijt wilt. Dit blijft van jou."
        />
      </div>

      <TagsInput
        tags={values.tags}
        onChange={(tags) => patch({ tags })}
        disabled={disabled}
      />
    </div>
  )
}
